import { useState, type MouseEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { StatusChip, type StatusTone } from '../data/StatusChip';
import type { Breadcrumb } from './PageHeader';
import { cardSurfaceSx } from './surface';

export interface EntityMeta {
  readonly label: string;
  readonly value: ReactNode;
}

/**
 * The way back to the list a detail page belongs to. `href` comes from a typed link builder
 * (`links.*` in `@eis/core`); `onNavigate` lets the caller route client-side, the same pairing the
 * modules use for every other in-app link.
 */
export interface EntityBackLink {
  readonly label: string;
  readonly href: string;
  readonly onNavigate?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export interface EntityHeaderProps {
  readonly title: string;
  /** The one-line identity of the thing: partner, product, owner. */
  readonly subtitle?: ReactNode;
  readonly status?: { readonly value: string; readonly label: string; readonly tone?: StatusTone };
  /** Key/value facts shown inline — kept short; everything else belongs in a DetailsSection. */
  readonly meta?: readonly EntityMeta[];
  readonly breadcrumbs?: readonly Breadcrumb[];
  /** Primary and secondary actions, right-aligned. Gate each with <RequirePermission>. */
  readonly actions?: ReactNode;
  /**
   * The overflow: `MenuItem`s behind a "Weitere Aktionen" button after `actions`; the menu closes
   * on any click inside it. A persona granted none of the items gets `undefined` from the caller
   * (decided with `usePermissions().can`), so no empty menu is ever offered.
   */
  readonly moreActions?: ReactNode;
  /** A small "← label" link above the header, back to the list (berry has no breadcrumb band). */
  readonly backTo?: EntityBackLink;
  /** A 40px icon tile before the title — the entity kind's glyph, decorative. */
  readonly icon?: ReactNode;
  /** Metadata badges under the meta row: the bound version, the kind. */
  readonly chips?: ReactNode;
}

/**
 * Header for a single entity. Distinct from PageHeader because an entity page has to answer
 * "what am I looking at, and what state is it in" before anything else — the status chip and the
 * identifying subtitle are structural here, not decoration.
 *
 * In berry (EIS Design System, prompt 24 Phase 24.3; `theme.eis.surface.headerCard`) it is a white
 * header card: icon tile, title with its status chip inline, a meta row of `Label: value` facts
 * separated by vertical rules, the badges, and the actions on the right. Suite keeps the layout it
 * had, and shows the new optional parts in it.
 */
export function EntityHeader({
  title,
  subtitle,
  status,
  meta,
  breadcrumbs,
  actions,
  moreActions,
  backTo,
  icon,
  chips,
}: EntityHeaderProps): ReactNode {
  const { t } = useTranslation('patterns');
  const card = useTheme().eis.surface.headerCard;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const back = backTo !== undefined && (
    <Link
      href={backTo.href}
      onClick={backTo.onNavigate}
      variant="body2"
      color="text.secondary"
      underline="hover"
      data-testid="entity-back"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, alignSelf: 'flex-start' }}
    >
      <ChevronLeftIcon fontSize="small" aria-hidden />
      {backTo.label}
    </Link>
  );

  const crumbs = breadcrumbs !== undefined && breadcrumbs.length > 0 && (
    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.75rem' }}>
      {breadcrumbs.map((crumb) =>
        crumb.to === undefined ? (
          <Typography key={crumb.label} variant="caption" color="text.secondary">
            {crumb.label}
          </Typography>
        ) : (
          <Link key={crumb.label} href={crumb.to} variant="caption" color="text.secondary">
            {crumb.label}
          </Link>
        ),
      )}
    </Breadcrumbs>
  );

  const actionRow = (actions !== undefined || moreActions !== undefined) && (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', flexShrink: 0 }}>
      {actions}
      {moreActions !== undefined && (
        <>
          <Button
            variant="contained"
            endIcon={<ExpandMoreIcon />}
            aria-haspopup="menu"
            aria-expanded={menuAnchor !== null}
            onClick={(event) => {
              setMenuAnchor(event.currentTarget);
            }}
          >
            {t('actions.more')}
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={menuAnchor !== null}
            onClose={() => {
              setMenuAnchor(null);
            }}
            onClick={() => {
              setMenuAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {moreActions}
          </Menu>
        </>
      )}
    </Stack>
  );

  const titleRow = (
    <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="h1" component="h1" sx={{ minWidth: 0 }}>
        {title}
      </Typography>
      {status !== undefined && <StatusChip status={status.value} label={status.label} tone={status.tone} />}
    </Stack>
  );

  const subtitleLine = subtitle !== undefined && (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
      {subtitle}
    </Typography>
  );

  const chipRow = chips !== undefined && (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
      {chips}
    </Stack>
  );

  if (!card) {
    return (
      <Stack spacing={1}>
        {back}
        {crumbs}
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, alignItems: 'flex-start' }}>
            {icon !== undefined && <IconTile>{icon}</IconTile>}
            <Box sx={{ minWidth: 0 }}>
              {titleRow}
              {subtitleLine}
            </Box>
          </Stack>
          {actionRow}
        </Stack>

        {meta !== undefined && meta.length > 0 && (
          <Stack
            direction="row"
            spacing={3}
            useFlexGap
            component="dl"
            sx={{ flexWrap: 'wrap', m: 0, mt: 0.5 }}
          >
            {meta.map((item) => (
              <Box key={item.label}>
                <Typography variant="caption" color="text.secondary" component="dt">
                  {item.label}
                </Typography>
                <Typography variant="body2" component="dd" sx={{ m: 0, fontWeight: 600 }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
        {chipRow}
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      {back}
      {crumbs}
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          // The facts wrap inside their own block; the actions keep the right edge from `md` up.
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{ minWidth: 0, flex: '1 1 auto', alignItems: 'flex-start' }}
          >
            {icon !== undefined && <IconTile>{icon}</IconTile>}
            <Box sx={{ minWidth: 0 }}>
              {titleRow}
              {subtitleLine}
              {meta !== undefined && meta.length > 0 && (
                <Stack
                  direction="row"
                  component="dl"
                  useFlexGap
                  divider={<Divider orientation="vertical" flexItem aria-hidden />}
                  spacing={1.5}
                  sx={{ flexWrap: 'wrap', m: 0, mt: 0.75, rowGap: 0.5 }}
                >
                  {meta.map((item) => (
                    <Box
                      key={item.label}
                      sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        component="dt"
                        // The colon is drawn, not written: the term stays the label itself.
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          '&::after': { content: '":"' },
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        component="dd"
                        sx={{ m: 0, fontSize: '0.875rem' }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
              {chipRow}
            </Box>
          </Stack>
          {actionRow}
        </Stack>
      </Paper>
    </Stack>
  );
}

/** The 40px tile in front of the title: soft primary ground, the badge radius. */
function IconTile({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        width: 40,
        height: 40,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: `${String(theme.eis.surface.badgeRadius)}px`,
        bgcolor: 'var(--mui-palette-surfaces-selected)',
        color: 'primary.light',
        '& svg': { fontSize: 22 },
      })}
    >
      {children}
    </Box>
  );
}
